import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getGrossMarginInventoryCycle, getGrossMarginReport } from "src/api/sp";
import { resetStore } from "src/store/actions";
import {
  getFormatedDate,
  getFormattedDateFromDifferentFormate,
  getMaxDateForGMR,
  isAfterDate
} from "src/utils/getDates";
import {
  handleDateRangeNotificationError,
  initialState,
  handleResetGrossMarginReport,
  handleFullfillFetchGrossMarginReport
} from "./GMRreducers";

export const fetchGrossMarginReport = createAsyncThunk(
  "fetchGrossMarginReport",
  (payload: any) => getGrossMarginReport(payload)
);

export const fetchInventoryCycle = createAsyncThunk(
  "/grossMarginReport/default",
  (storeId: string) => getGrossMarginInventoryCycle(storeId)
);

const grossMarginReportSlice = createSlice({
  name: "grossMarginReport",
  initialState,
  reducers: {
    handleResetGrossMarginReport,
    handleDateRangeNotificationError,
    updateDateRange(state, action) {
      const { fromDate, toDate } = action.payload;
      if (fromDate) {
        state.fromDate = fromDate;
      }
      if (toDate) {
        state.toDate = toDate;
      }
    },
    updateTab(state, action) {
      const { tabIndex } = action.payload;
      state.tabIndex = tabIndex;
    },
    updateFinization(state, action) {
      const { finalizeAction } = action.payload;
      state.finalizeAction = finalizeAction;
    },
    inventoryCycleUpdate(state) {
      state.fromDate = state.inventorycycleFromDate;
      state.toDate = state.inventorycycleToDate;
    },
    updateDates(state, action) {
      const { fromDate, toDate } = action.payload;
      state.fromDate = fromDate;
      state.toDate = toDate;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchGrossMarginReport.pending, (state) => {
        state.apiStatus = "pending";
        state.apiData = {};
        state.salesAndReceiptData = [];
        state.marginImpactData = [];
        state.trendReportData = [];
        state.commissionCalculationData = [];
        state.caCommissionCalculationData = [];
      })
      .addCase(
        fetchGrossMarginReport.fulfilled,
        handleFullfillFetchGrossMarginReport
      )
      .addCase(fetchGrossMarginReport.rejected, (state, action) => {
        if (action?.error?.message === "ERR_CANCELED") {
          state.apiStatus = "pending";
        } else {
          state.apiStatus = "error";
        }
      })
      .addCase(fetchInventoryCycle.pending, (state) => {
        state.inventoryCycleApiStatus = "pending";
      })
      .addCase(fetchInventoryCycle.fulfilled, (state, action) => {
        try {
          const { data } = action.payload;
          const fromDate = getFormattedDateFromDifferentFormate(
            data?.startDate,
            "DD-MM-YYYY",
            "MM/DD/YY"
          );
          const toDate = getFormattedDateFromDifferentFormate(
            data?.endDate,
            "DD-MM-YYYY",
            "MM/DD/YY"
          );
          const maxDateForGMR = getMaxDateForGMR();

          if (isAfterDate(fromDate, maxDateForGMR.toString())) {
            state.initialFromDate = getFormatedDate(maxDateForGMR, "MM/DD/YY");
          } else {
            state.initialFromDate = fromDate;
          }

          if (data.isOngoing) {
            const toDate = getFormatedDate(maxDateForGMR, "MM/DD/YY");
            state.initialToDate = toDate;
            state.inventorycycleToDate = toDate;
          } else {
            state.initialToDate = toDate;
            state.inventorycycleToDate = toDate;
          }
          state.inventorycycleFromDate = fromDate;
          state.inventoryCycleApiStatus = "idle";
        } catch {
          state.inventoryCycleApiStatus = "error";
        }
      })
      .addCase(fetchInventoryCycle.rejected, (state) => {
        state.inventoryCycleApiStatus = "error";
      });
  }
});

export const {
  updateDateRange,
  updateTab,
  updateFinization,
  inventoryCycleUpdate,
  updateDates,
  handleResetGrossMarginReport: resetGrossMarginReport,
  handleDateRangeNotificationError: updateDateRangeNotificationError
} = grossMarginReportSlice.actions;
export const getTabIndex = (state: any) => {
  return state.grossMarginReport.tabIndex;
};

export const getFinalizeAction = (state: any) => {
  return state.grossMarginReport.finalizeAction;
};

export const getTrendReportHighlights = (state: any) => {
  return state.grossMarginReport.trendReportHighlights;
};

export const getAPIStatus = (state: any) => {
  return state.grossMarginReport.apiStatus;
};

export const getDates = (state: any) => {
  return {
    fromDate: state.grossMarginReport.fromDate,
    toDate: state.grossMarginReport.toDate
  };
};

export const getInitialDates = (state: any) => {
  return {
    initialFromDate: state.grossMarginReport.initialFromDate,
    initialToDate: state.grossMarginReport.initialToDate
  };
};

export const getInventoryCycleDates = (state: any) => {
  return {
    inventorycycleFromDate: state.grossMarginReport?.inventorycycleFromDate,
    inventorycycleToDate: state.grossMarginReport?.inventorycycleToDate
  };
};

export default grossMarginReportSlice.reducer;
